import React from 'react';
import { Box, Typography } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useTranslation } from 'react-i18next';

import type { ScriptStep } from '@shared/interfaces/assistantScripts';

import {
  estimateStepSeconds,
  formatSeconds,
  hasQuiz,
} from './scriptData';
import { useAiInspectorStyles } from './inspectorStyles';

const QUIZ_LABEL_KEY: Record<
  'multiple_choice' | 'text_input' | 'none',
  { key: string; fallback: string }
> = {
  multiple_choice: {
    key: 'editorMode.aiQuizMultiple',
    fallback: 'Multiple choice',
  },
  text_input: {
    key: 'editorMode.aiQuizTextInput',
    fallback: 'Text input',
  },
  none: { key: 'editorMode.aiQuizNone', fallback: 'No quiz' },
};

interface IScriptTimelineProps {
  steps: ScriptStep[];
}

const ScriptTimeline = ({ steps }: IScriptTimelineProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();

  if (steps.length === 0) {
    return (
      <Typography sx={classes.emptyHint}>
        {translate(
          'editorMode.aiTimelineEmpty',
          'No steps yet — open the editor to add one.',
        )}
      </Typography>
    );
  }

  return (
    <Box sx={classes.timeline} role="list">
      {steps.map((step, index) => {
        const text =
          typeof step.text === 'string' && step.text.trim().length > 0
            ? step.text
            : translate(
                'editorMode.aiStepEmpty',
                '(empty step — write the first line Noa will say)',
              );
        const seconds = estimateStepSeconds(step.text ?? '');
        const quizType = step.questionary?.type ?? 'none';
        const quizLabel = QUIZ_LABEL_KEY[quizType];

        return (
          <Box
            key={`step-${index}`}
            sx={classes.step}
            role="listitem"
            aria-label={translate('editorMode.aiStepN', 'Step {{n}}', {
              n: index + 1,
            })}
          >
            <Typography sx={classes.stepText}>{text}</Typography>
            <Box sx={classes.stepMeta}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 10 }} />~
              {formatSeconds(seconds)}
              {hasQuiz(step) ? (
                <Box component="span" sx={classes.stepQuiz}>
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 10 }} />
                  {translate(quizLabel.key, quizLabel.fallback)}
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ScriptTimeline;
